import { useEffect, useMemo, useState } from 'react';
import { ENDPOINTS } from '../services/apiService';
import { transformData as transformFacilityData } from './utils';
import { useDataContext } from '../context/DataContext';
import { EmisEntry } from './types';
import { useLoadingStore } from '../store/loadingStore';
type EmissionMid = {
  eemdl_id: number;
  facility_type: string;
  oil_and_gas: boolean;
  oil_and_gas_facility_type: string | null;
  emissions: number;
  cumulative_emissions: number;
  cum_frac_ER: number;
  'Leaker/Facility Type': number;
  'Population/Facility Type': number;
};

type OutputMidData = {
  type: string;
  emissions: Emission[];
};

function mergeAllToType(
  data: { type: string; emissions: EmisEntry[] }[],
  targetType: string,
): OutputMidData {
  const allEmissions: EmissionMid[] = [];

  data.forEach((item) => {
    const filteredEmissions = item.emissions.filter(
      (emission) => String(emission.oil_and_gas_facility_type) === targetType,
    );

    const emissionsAsMid: EmissionMid[] = filteredEmissions.map((emission) => ({
      eemdl_id: emission.eemdl_id,
      facility_type: String(emission.facility_type),
      oil_and_gas: Boolean(emission.oil_and_gas),
      oil_and_gas_facility_type: emission.oil_and_gas_facility_type
        ? String(emission.oil_and_gas_facility_type)
        : null,
      emissions: emission.emissions,
      cumulative_emissions: emission.cumulative_emissions,
      cum_frac_ER: emission.cum_frac_ER,
      'Leaker/Facility Type': emission['Leaker/Facility Type'],
      'Population/Facility Type': emission['Population/Facility Type'],
    }));

    allEmissions.push(...emissionsAsMid);
  });

  return { type: targetType, emissions: allEmissions };
}

type Emission = {
  eemdl_id: number;
  facility_type: string;
  oil_and_gas: boolean;
  emissions: number;
  cumulative_emissions: number;
  cum_frac_ER: number;
  'Leaker/Facility Type': number;
  'Population/Facility Type': number;
};

type InputData = {
  type: string;
  emissions: any[];
};

type OutputData = {
  'Updated Equipment Type': string;
  TotalEmissionRate: number;
  PercentageOfTotal: number;
  EquipmentCount: number;
}[];

function aggregateEmissionsFromMultipleArrays(
  ...data: InputData[]
): OutputData {
  const totalEmission = data.reduce((total, item) => {
    const typeTotal = item.emissions.reduce(
      (sum, emission) => sum + emission.emissions,
      0,
    );
    return total + typeTotal;
  }, 0);

  const result = data.map((item) => {
    const typeTotalEmission = item.emissions.reduce(
      (sum, emission) => sum + emission.emissions,
      0,
    );
    const equipmentCount = item.emissions.length;

    return {
      'Updated Equipment Type': item.type,
      TotalEmissionRate: typeTotalEmission,
      PercentageOfTotal: (typeTotalEmission / totalEmission) * 100,
      EquipmentCount: equipmentCount,
    };
  });

  return result;
}

export const useComparisonLevel = ({
  period,
  time,
}: {
  period: string;
  time: string;
}) => {
  const yearsArray = useMemo(() => {
    if (period.includes('All')) {
      return ['2024'];
    } else {
      const periodParts = period.split(' ');
      return [periodParts[1]];
    }
  }, [period]);

  const quartersArray = useMemo(() => {
    if (period.includes('All')) {
      return ['1', '2', '3', '4'];
    } else {
      const periodParts = period.split(' ');
      return [periodParts[0].slice(1)];
    }
  }, [period]);

  const [allResults, setAllResults] = useState<any[]>([]);
  const [chart1Results, setChart1Results] = useState<any[]>([]);
  const [chart2Results, setChart2Results] = useState<any[]>([]);
  const [chart3Results, setChart3Results] = useState<any[]>([]);
  const [upstreamResults, setUpstreamResults] = useState<any[]>([]);
  const [midstreamResults, setMidstreamResults] = useState<any[]>([]);
  const [totalResults, setTotalResults] = useState<any[]>([]);

  const { fetchMultipleAndSetData } = useDataContext();
  const setLoading = useLoadingStore(
    (state: { setLoading: any }) => state.setLoading,
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const results = await fetchMultipleAndSetData([
          {
            type: ENDPOINTS.facility_data,
            params: {
              quarter: quartersArray.join(','),
              year: yearsArray.join(','),
              sponsored: '1', // ALL AMI DATA
              og: '1',
              facility_type: time,
            },
          },
          {
            type: ENDPOINTS.facility_data,
            params: {
              quarter: quartersArray.join(','),
              year: yearsArray.join(','),
              og: '0', // ALL NONE OG DATA
              facility_type: time,
            },
          },
          {
            type: ENDPOINTS.facility_data,
            params: {
              quarter: quartersArray.join(','),
              year: yearsArray.join(','),
              sponsored: '1', // ALL AMI DATA
              og: '1',
              facility_type: 'Upstream',
            },
          },
          {
            type: ENDPOINTS.facility_data,
            params: {
              quarter: quartersArray.join(','),
              year: yearsArray.join(','),
              sponsored: '1', // ALL AMI DATA
              og: '1',
              facility_type: 'Midstream',
            },
          },
        ]);

        if (results.length > 0) {
          const calcArData = results.find(
            (r) => r.type === ENDPOINTS.facility_data + '#' + 0,
          )?.data;

          const noneOGData = results.find(
            (r) => r.type === ENDPOINTS.facility_data + '#' + 1,
          )?.data;

          const upstreamData = results.find(
            (r) => r.type === ENDPOINTS.facility_data + '#' + 2,
          )?.data;

          const midstreamData = results.find(
            (r) => r.type === ENDPOINTS.facility_data + '#' + 3,
          )?.data;

          const transformedData = transformFacilityData(calcArData);
          const transformedNoneOGData = transformFacilityData(noneOGData);
          const transformedUpstreamData = transformFacilityData(upstreamData);
          const transformedMidstreamData = transformFacilityData(midstreamData);

          const nonOGCoalData =
            transformedNoneOGData &&
            transformedNoneOGData.filter((data) => data.type.includes('Coal'));

          const nonOGLandfillData =
            transformedNoneOGData &&
            transformedNoneOGData.filter((data) =>
              data.type.includes('Landfill'),
            );

          const nonOGAnimalData =
            transformedNoneOGData &&
            transformedNoneOGData.filter((data) =>
              data.type.includes('Animal'),
            );

          const renamedMid =
            transformedMidstreamData &&
            mergeAllToType(transformedMidstreamData, 'Midstream');

          const renamedUp =
            transformedUpstreamData &&
            mergeAllToType(transformedUpstreamData, 'Upstream');

          const renamedUpAsArray = renamedUp && [
            { type: renamedUp.type, emissions: renamedUp.emissions },
          ];
          const renamedMidAsArray = renamedMid && [
            { type: renamedMid.type, emissions: renamedMid.emissions },
          ];

          const total =
            nonOGCoalData &&
            nonOGAnimalData &&
            renamedUpAsArray &&
            renamedMidAsArray &&
            nonOGLandfillData &&
            aggregateEmissionsFromMultipleArrays(
              ...renamedUpAsArray,
              ...renamedMidAsArray,
              ...nonOGCoalData,
              ...nonOGAnimalData,
              ...nonOGLandfillData,
            );

          setAllResults(transformedData || []);
          setChart1Results(nonOGCoalData || []);
          setChart2Results(nonOGAnimalData || []);
          setChart3Results(nonOGLandfillData || []);
          setUpstreamResults(renamedUpAsArray || []);
          setMidstreamResults(renamedMidAsArray || []);
          setTotalResults(total || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      } finally {
        setTimeout(() => setLoading(false), 7000);
      }
    };

    fetchData();
  }, [
    fetchMultipleAndSetData,
    period,
    time,
    quartersArray,
    yearsArray,
    setLoading,
  ]);

  return {
    allResults,
    chart1Results,
    chart2Results,
    chart3Results,
    upstreamResults,
    midstreamResults,
    totalResults,
  };
};
